import moment from 'moment';
import { formatNumber } from '@equifinance/shared';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import {
  CreditItem, Insolvency, NameMatchedData,
  NonRevolvingAccountTypes,
  PaymentHistory,
} from '../../../../contexts/credit';
import {
  CreditDetailsBottomContainer,
  CreditDetailsEquifax,
  CreditDetailsOutput,
  CreditDetailsPaymentHistoryContainer,
  CreditDetailsUserOutputChild,
  CreditDetailsUserOutputChildTitle,
  CreditDetailsUserOutputContainer,
  CreditDetailsUserOutputContainerText,
  CreditDetailsUserOutputContainerTextTitle,
  CreditDetailsUserOutputContainerTitle,
  CreditHistoryBox,
  CreditHistoryBoxAge,
  CreditHistoryBoxContainer,
  CreditHistoryContainer,
  InsolvencyEquifax,
  Title,
} from '../CreditDetails';

function CreditDetailsHeader() {
  return (
    <>
      <Title>Equifax</Title>

      <CreditDetailsUserOutputContainerTitle>
        <CreditDetailsUserOutputChildTitle>
          <CreditDetailsUserOutputContainerTextTitle>Matched name</CreditDetailsUserOutputContainerTextTitle>
          <CreditDetailsUserOutputContainerTextTitle>Date of birth</CreditDetailsUserOutputContainerTextTitle>
        </CreditDetailsUserOutputChildTitle>
        <CreditDetailsUserOutputChildTitle>
          <CreditDetailsUserOutputContainerTextTitle>Match type</CreditDetailsUserOutputContainerTextTitle>
        </CreditDetailsUserOutputChildTitle>
      </CreditDetailsUserOutputContainerTitle>
    </>
  );
}

function CreditDetailsUserDetails({ nameMatchedData }: {nameMatchedData: NameMatchedData[]}) {
  return (
    <>
      {
        nameMatchedData?.map(({
          firstName = '', lastName = '', dateOfBirth = '', nameMatchStatus, nameMatchDescription = '',
        }) => (
          <CreditDetailsUserOutputContainer key={`${firstName}-${lastName}-${nameMatchStatus}`}>
            <CreditDetailsUserOutputChild>
              <CreditDetailsUserOutputContainerText>{`${firstName} ${lastName}`}</CreditDetailsUserOutputContainerText>
              <CreditDetailsUserOutputContainerText>
                {dateOfBirth ? moment(dateOfBirth).format('DD/MM/yyyy') : '-'}
              </CreditDetailsUserOutputContainerText>
            </CreditDetailsUserOutputChild>
            <CreditDetailsUserOutputChild>
              <CreditDetailsUserOutputContainerText>{nameMatchDescription}</CreditDetailsUserOutputContainerText>
            </CreditDetailsUserOutputChild>
          </CreditDetailsUserOutputContainer>
        ))
      }
    </>
  );
}

export default function EquifaxDetails({
  creditItem,
  insolvencyItem,
  creditType,
}: {
  creditItem?: CreditItem;
  insolvencyItem?: Insolvency;
  creditType?: 'secured' | 'unsecured';
}) {
  const formatDateValue = (value: string) => {
    if (!value || !moment(value).isValid()) {
      return '-';
    }
    return formatDateTime(value, DateFormatStringEnum.DDMMYYYY);
  };

  const formatNumberValue = (value: number) => {
    if (value == null || Number.isNaN(value)) {
      return '-';
    }

    return `£ ${formatNumber(value, 2)}`;
  };

  const histories: PaymentHistory[] = Array.from({ length: 48 }, (_v, k) => creditItem?.paymentHistory?.[k] || { ageInMonths: k, status: '' });

  return (
    <>
      {creditItem?.isEquifax && (
        <CreditDetailsEquifax>
          <CreditDetailsBottomContainer>
            <CreditDetailsHeader />

            <CreditDetailsUserDetails nameMatchedData={creditItem?.nameMatchedData} />

            <CreditDetailsOutput
              label="Company type"
              value={creditItem?.companyClassDescription || '-'}
            />
            <CreditDetailsOutput
              label="Credit type"
              value={creditItem?.bureauCreditItemTypeDescriptor || '-'}
            />
            <CreditDetailsOutput
              label="Start date"
              value={formatDateValue(creditItem?.startDate)}
            />
            <CreditDetailsOutput
              label="Default date"
              value={formatDateValue(creditItem?.defaultDate)}
            />
            <CreditDetailsOutput
              label="Update date"
              value={formatDateValue(creditItem?.updateDate)}
            />
            <CreditDetailsOutput
              label="End date"
              value={formatDateValue(creditItem?.endDate)}
            />
            <CreditDetailsOutput
              label="Credit term"
              value={
                  creditItem?.paymentTerms != null
                    ? `${creditItem.paymentTerms} months`
                    : '-'
                }
            />
            <CreditDetailsOutput
              label="Start balance / Credit limit"
              value={formatNumberValue(
                creditType === 'secured' || NonRevolvingAccountTypes.includes(creditItem?.accountType)
                  ? creditItem?.openingBalance
                  : creditItem?.creditLimit,
              )}
            />
            <CreditDetailsOutput
              label="Default / Delinquent balance"
              value={formatNumberValue(creditItem?.defaultBalance)}
            />
            <CreditDetailsOutput
              label="Current balance"
              value={formatNumberValue(creditItem?.balance)}
            />
            <CreditDetailsOutput
              label="Supplementary indicator"
              value={
                  creditItem?.supplementaryInformation
                    ? creditItem.supplementaryInformation
                    : '-'
                }
            />
            <CreditDetailsOutput
              label="Payment arrangement"
              value={
                  creditItem?.monthlyPayment != null
                  && creditItem?.paymentFrequency
                    ? `${formatNumberValue(creditItem.monthlyPayment)} ${
                      creditItem.paymentFrequency.toLowerCase()
                    }`
                    : '-'
                }
            />
          </CreditDetailsBottomContainer>

          <CreditDetailsBottomContainer>
            <Title>Payment history</Title>
            <CreditDetailsPaymentHistoryContainer>
              <CreditHistoryContainer>
                {histories.slice(0, 24).map((history) => (
                  <CreditHistoryBoxContainer key={history.ageInMonths}>
                    <CreditHistoryBoxAge>{history.ageInMonths + 1}</CreditHistoryBoxAge>
                    <CreditHistoryBox status={history.status}>{history.status}</CreditHistoryBox>
                  </CreditHistoryBoxContainer>
                ))}
              </CreditHistoryContainer>
              <CreditHistoryContainer>
                {histories.slice(24, 48).map((history) => (
                  <CreditHistoryBoxContainer key={history.ageInMonths}>
                    <CreditHistoryBoxAge>{history.ageInMonths + 1}</CreditHistoryBoxAge>
                    <CreditHistoryBox status={history.status}>{history.status}</CreditHistoryBox>
                  </CreditHistoryBoxContainer>
                ))}
              </CreditHistoryContainer>
            </CreditDetailsPaymentHistoryContainer>
          </CreditDetailsBottomContainer>
        </CreditDetailsEquifax>
      )}

      {insolvencyItem?.isEquifax && (
        <InsolvencyEquifax>
          <CreditDetailsBottomContainer>
            <CreditDetailsHeader />

            <CreditDetailsUserDetails nameMatchedData={insolvencyItem?.nameMatchedData} />

            <CreditDetailsOutput
              label="Court date"
              value={formatDateValue(insolvencyItem.courtDate)}
            />
            <CreditDetailsOutput
              label="Satisfied date"
              value={insolvencyItem.hasSatisfiedDateFromEquifax ? formatDateValue(insolvencyItem.satisfiedDate) : '-'}
            />
            <CreditDetailsOutput
              label="Type"
              value={insolvencyItem.equifaxType || '-'}
            />
            <CreditDetailsOutput
              label="Value"
              value={formatNumberValue(insolvencyItem.value)}
            />
            <CreditDetailsOutput
              label="Court details"
              value={insolvencyItem.courtDetails || '-'}
            />
          </CreditDetailsBottomContainer>
        </InsolvencyEquifax>
      )}
    </>
  );
}
